import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Shield as ShieldIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

// ============================================
// Style constants
// ============================================
const colors = {
  bg: '#111827',
  cardBg: '#1f2937',
  border: '#374151',
  primary: '#B25373',
  primaryHover: '#8E3D59',
  textWhite: '#ffffff',
  textGray300: '#d1d5db',
  textGray400: '#9ca3af',
  textGray500: '#6b7280',
  danger: '#ef4444',
  dangerLight: '#fca5a5',
  dangerBg: 'rgba(239, 68, 68, 0.1)',
  infoBg: 'rgba(2, 132, 199, 0.1)',
  infoIcon: '#0ea5e9',
  infoText: '#7dd3fc',
};

// ============================================
// OTP Verification Component
// ============================================

const OtpVerification: React.FC = () => {
  const navigate = useNavigate();
  const { verifyOtp, resendOtp, error, isLoading, clearError } = useAuth();

  const [identifier, setIdentifier] = useState<string>('');
  const [maskedPhone, setMaskedPhone] = useState<string>('');
  const [otpCode, setOtpCode] = useState('');
  const [countdown, setCountdown] = useState(120);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    const storedIdentifier = sessionStorage.getItem('auth_identifier');
    if (!storedIdentifier) {
      navigate('/login');
      return;
    }
    setIdentifier(storedIdentifier);
    setMaskedPhone(sessionStorage.getItem('auth_masked_phone') || storedIdentifier);

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (otpCode.trim() !== '') {
      try {
        const success = await verifyOtp(identifier, otpCode);
        if (success) {
          navigate('/');
        }
      } catch (err) {
        // Error handled by context
      }
    }
  };

  const handleResendOtp = async () => {
    if (canResend) {
      try {
        const success = await resendOtp(identifier);
        if (success) {
          setCanResend(false);
          setCountdown(120);

          const timer = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(timer);
                setCanResend(true);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }
      } catch (err) {
        // Error handled by context
      }
    }
  };

  const formatCountdown = () => {
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const otpInputStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    padding: '14px 16px',
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    backgroundColor: colors.cardBg,
    color: colors.textWhite,
    fontSize: 24,
    fontFamily: 'inherit',
    textAlign: 'center',
    letterSpacing: '0.3em',
    outline: 'none',
    direction: 'ltr',
    boxSizing: 'border-box',
  };

  const buttonBaseStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'inherit',
    cursor: 'pointer',
  };

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.bg,
        padding: '48px 16px',
      }}
    >
      <Box
        sx={{
          maxWidth: 448,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <ShieldIcon sx={{ fontSize: 40, color: '#fff' }} />
          </Box>
          <Typography
            sx={{
              fontSize: 30,
              fontWeight: 800,
              color: colors.textWhite,
              lineHeight: 1.3,
            }}
          >
            تایید هویت دو مرحله‌ای
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: 14,
              color: colors.textGray400,
            }}
          >
            کد تایید پیامک‌شده به موبایل خود را وارد کنید
          </Typography>
        </Box>

        {/* Phone Info */}
        <Box
          sx={{
            borderRadius: 2,
            p: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            backgroundColor: colors.infoBg,
          }}
        >
          <InfoIcon sx={{ color: colors.infoIcon, fontSize: 20, mt: 0.25, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 14, color: colors.infoText, lineHeight: 1.6 }}>
            کد تایید به شماره موبایل{' '}
            <strong dir="ltr" style={{ fontFamily: 'monospace' }}>
              {maskedPhone}
            </strong>{' '}
            پیامک شده است. لطفا کد ۶ رقمی دریافتی را وارد کنید.
          </Typography>
        </Box>

        {/* Error Message */}
        {error && (
          <Box
            sx={{
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              backgroundColor: colors.dangerBg,
            }}
          >
            <WarningIcon sx={{ color: colors.danger, fontSize: 20, mt: 0.25, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 14, color: colors.dangerLight, lineHeight: 1.6 }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* OTP Form */}
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography
              component="label"
              htmlFor="otp-code"
              sx={{
                display: 'block',
                fontSize: 14,
                fontWeight: 500,
                color: colors.textGray300,
                mb: 0.5,
              }}
            >
              کد تایید
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <input
                id="otp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                maxLength={6}
                autoFocus
                style={otpInputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = colors.primary;
                  e.target.style.boxShadow = '0 0 0 2px rgba(178, 83, 115, 0.3)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = colors.border;
                  e.target.style.boxShadow = 'none';
                }}
              />
            </Box>
            <Typography
              sx={{
                mt: 1,
                fontSize: 13,
                color: colors.textGray400,
                textAlign: 'center',
              }}
            >
              {canResend
                ? 'کد را دریافت نکرده‌اید؟'
                : `ارسال مجدد کد تا ${formatCountdown()}`}
            </Typography>
          </Box>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading || otpCode.length < 6}
            style={{
              ...buttonBaseStyle,
              padding: '10px 16px',
              fontWeight: 500,
              color: colors.textWhite,
              backgroundColor: colors.primary,
              boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
              opacity: isLoading || otpCode.length < 6 ? 0.5 : 1,
              cursor: isLoading || otpCode.length < 6 ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => {
              if (!isLoading && otpCode.length >= 6)
                (e.target as HTMLElement).style.backgroundColor = colors.primaryHover;
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.backgroundColor = colors.primary;
            }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ color: '#fff' }} />
                <span>در حال تایید...</span>
              </>
            ) : (
              'تایید و ورود'
            )}
          </button>

          {/* Resend */}
          <button
            type="button"
            onClick={handleResendOtp}
            disabled={!canResend || isLoading}
            style={{
              ...buttonBaseStyle,
              padding: '8px 16px',
              color: colors.textGray400,
              backgroundColor: 'transparent',
              opacity: !canResend || isLoading ? 0.5 : 1,
              cursor: !canResend || isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            ارسال مجدد کد
          </button>

          {/* Back to Login */}
          <button
            type="button"
            onClick={() => navigate('/login')}
            style={{
              ...buttonBaseStyle,
              padding: '8px 16px',
              color: colors.textGray400,
              backgroundColor: 'transparent',
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            بازگشت به صفحه ورود
          </button>
        </Box>

        {/* Security Notice */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: 12, color: colors.textGray500, lineHeight: 1.8 }}>
            این بخش امن مدیریت است.
          </Typography>
          <Typography sx={{ fontSize: 12, color: colors.textGray500, lineHeight: 1.8 }}>
            تمام فعالیت‌ها ثبت و نظارت می‌شوند.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default OtpVerification;
